/**
 * Shared Sentry event policy: which environments may report, and what must be
 * stripped before an event leaves the process.
 *
 * WHY IT IS SHARED. This logic was copy-pasted into every `sentry.*.config.ts`
 * in the estate (ten server configs, ten client configs, the API tracker and the
 * functions bootstrap). Adding edge configs would have made it thirty copies of
 * a rule that decides whether customer PII reaches a third party — the exact
 * shape that drifts silently, because each copy keeps working while they stop
 * agreeing. It lives in `@order-up/analytics` (already the owner of
 * `SentryErrorTracker`) behind its own `./sentry` export subpath with NO
 * internal imports, so a config file loaded at process boot does not drag the
 * PostHog/forecast graph in with it.
 *
 * `apps/functions` deliberately keeps its own copy: it is a self-contained
 * submodule that takes no workspace dependency, which is a stated architectural
 * boundary rather than an oversight.
 *
 * The environment is passed IN rather than read from `process.env` here. Server
 * code reads `VERCEL_ENV`; browser bundles can only see `NEXT_PUBLIC_VERCEL_ENV`
 * because Next.js inlines nothing else. A helper that guessed would be wrong on
 * one side of that line, and pure inputs make the policy unit-testable.
 *
 * The imports below (`../redaction`, `../giftCardNumberRedaction`,
 * `../sensitiveKeys`) are leaf modules with NO imports of their own — they do
 * not reopen the "config file at process boot drags the graph in" problem this
 * file's isolation exists to avoid.
 */

import { redactCapabilityPaths, redactUrlShapedValues } from '../redaction';
import {
  isGiftCardPath,
  redactCardNumbersDeep,
  redactPaymentCardNumbersDeep,
} from '../giftCardNumberRedaction';
import { isSensitiveKey } from '../sensitiveKeys';

export {
  isUrlShapedKey,
  REDACTED_CAPABILITY_TOKEN,
  redactCapabilityPaths,
  redactUrlShapedValues,
} from '../redaction';
export {
  containsPaymentCardNumber,
  isGiftCardPath,
  looksLikePaymentCardNumber,
  maskedCardNumber,
  passesLuhn,
  redactCardNumberRuns,
  redactCardNumbersDeep,
  redactCardNumberUrl,
  redactGiftCardUrl,
  redactPaymentCardNumbers,
  redactPaymentCardNumbersDeep,
} from '../giftCardNumberRedaction';
export {
  isSensitiveKey,
  normalizeSensitiveKey,
  SENSITIVE_EXACT_KEYS,
  SENSITIVE_KEY_FRAGMENTS,
} from '../sensitiveKeys';

/**
 * Environments whose events are dropped before they leave the process.
 *
 * ONLY ephemeral PR previews. The real deployment tiers are dev / staging /
 * prod and all three are WANTED in Sentry — you cannot debug a staging incident
 * from a dashboard that never received it.
 *
 * This list used to include `development`, `dev` and `test`, on the theory that
 * non-production events pollute the production project. They pollute the
 * ALERTS, which is a different problem with a different fix: the incidents
 * webhook drops any event whose environment is not `production`, so dev and
 * staging stay visible while the pager stays quiet. Dropping them at ingestion
 * threw away the data as well as the noise. Measured 2026-09-09 — `management`
 * took 1K `development` events in a day and alerted a person; the answer was
 * the alert gate, not blinding staging.
 *
 * A PR preview is not a tier. It exists for hours and its errors are noise
 * against every dashboard and against quota.
 */
export const DROPPED_ENVIRONMENTS: readonly string[] = ['preview'];

/**
 * True when `env` names an environment whose events must be dropped.
 *
 * Deliberately a DENYLIST, not `env !== 'production'`. An unresolved
 * environment (every var unset — a runtime we have not met yet) must fall
 * through and REPORT: a silent zero-event pipeline reads as "all clear" and is
 * the failure mode this estate has been bitten by repeatedly, whereas an
 * unexpected extra event is visible and cheap to filter in Sentry.
 */
export function isDroppedEnvironment(env: string | undefined | null): boolean {
  if (!env) return false;
  return DROPPED_ENVIRONMENTS.includes(env.toLowerCase());
}

/**
 * The subset of a Sentry event this policy reads or rewrites.
 *
 * Deliberately loose. The SDK's own `ErrorEvent` differs per package and per
 * major version (`user.id` is `string | number`, `user.ip_address` is
 * `string | null`), so a strict mirror of it here would fail to accept the very
 * types it exists to serve — and would have to be re-mirrored on every SDK bump.
 */
export interface ScrubbableSentryEvent {
  user?: {
    email?: unknown;
    username?: unknown;
    ip_address?: unknown;
    id?: unknown;
    [key: string]: unknown;
  };
  tags?: Record<string, unknown>;
  /** Where the URL lives on a browser or server event. */
  request?: {
    url?: unknown;
    headers?: Record<string, unknown>;
    [key: string]: unknown;
  };
  /** Navigation breadcrumbs carry the URL twice more, in `data.from` / `data.to`. */
  breadcrumbs?: Array<{ data?: Record<string, unknown>; [key: string]: unknown }>;
  /** A TRANSACTION's url lives here, as `data['url.full']`. */
  contexts?: {
    trace?: { data?: Record<string, unknown>; [key: string]: unknown };
    [key: string]: unknown;
  };
  /** And once per span, under the same OTel-style keys. */
  spans?: Array<{ data?: Record<string, unknown>; [key: string]: unknown }>;
}

/**
 * Every place a Sentry event carries a URL, redacted through the one shared
 * rule (`../redaction`). Mutates in place, matching `beforeSend`'s contract.
 *
 * Each site is needed and none substitutes for another:
 *
 *   - `request.url` is the page the event happened on. `httpContextIntegration`
 *     writes it on EVERY event, errors and transactions alike.
 *   - the `Referer` header is the page before it. Checked through
 *     `redactUrlShapedValues` so both casings and HTTP's one-r spelling are
 *     covered without a list.
 *   - a navigation breadcrumb records both ends of every route change in the
 *     session, so redacting only `request.url` would leave the token in the
 *     breadcrumb trail of the very NEXT error. `from`/`to` are named
 *     explicitly: neither is a url-shaped key.
 *   - `contexts.trace.data['url.full']` and each span's `data` are the TRACING
 *     side. With `tracesSampleRate: 0.1` the ordering app sends a transaction
 *     for roughly every tenth page view, and those were entirely raw while
 *     only `beforeSend` was wired (see {@link sentryEventScrubbers}).
 */
function redactEventUrls(event: ScrubbableSentryEvent): void {
  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = redactCapabilityPaths(event.request.url);
    }
    redactUrlShapedValues(event.request.headers);
  }

  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      const data = crumb?.data;
      if (!data) continue;
      // `from` and `to` are not url-shaped keys, so they are named.
      for (const key of ['from', 'to']) {
        if (typeof data[key] === 'string') {
          data[key] = redactCapabilityPaths(data[key] as string);
        }
      }
      redactUrlShapedValues(data);
    }
  }

  redactUrlShapedValues(event.contexts?.trace?.data);

  if (Array.isArray(event.spans)) {
    for (const span of event.spans) {
      redactUrlShapedValues(span?.data);
    }
  }
}

/**
 * Which keys are filtered is NOT decided here: `isSensitiveKey` (`../sensitiveKeys`)
 * is the one list core's logger reads too. This file kept its own regex until
 * 2026-10-29 — credentials and bank numbers only — so a 500 carried the SSN,
 * date of birth and PINs that the log line beside it had redacted.
 */
const FILTERED = '[Filtered]';

function redactSecretKeys(value: unknown, depth = 0): unknown {
  if (depth > 10 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) value[i] = redactSecretKeys(value[i], depth + 1);
    return value;
  }
  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    obj[key] = isSensitiveKey(key) ? FILTERED : redactSecretKeys(obj[key], depth + 1);
  }
  return obj;
}

/**
 * Review F13: @sentry/node attaches an incoming request's body as
 * `request.data`, so a 500 on PUT /mailbox carried a rep's mailbox password
 * to Sentry. Every secret-named key is redacted, at any depth, on EVERY route —
 * a per-route fix is only as good as the next route someone forgets. A body
 * that arrived as a string is parsed and redacted; one that cannot be parsed
 * but names a secret is dropped whole.
 */
function redactRequestBody(event: ScrubbableSentryEvent): void {
  const request = event.request;
  if (!request || request.data === undefined || request.data === null) return;
  const raw = request.data;
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed !== null && typeof parsed === 'object') {
        request.data = JSON.stringify(redactSecretKeys(parsed));
        return;
      }
    } catch {
      // not JSON — judged below
    }
    if (isSensitiveKey(raw)) request.data = FILTERED;
    return;
  }
  redactSecretKeys(raw);
}

/**
 * The shared `beforeSend`. Returns `null` to drop the event.
 *
 * 1. Drops ephemeral PR-preview events only. dev and staging are KEPT — they
 *    are real tiers, and alerting is gated separately at the incidents webhook.
 * 2. Strips the identity fields the SDK auto-populates from request context
 *    (`sendDefaultPii: false` does not cover a user object set explicitly
 *    elsewhere in the app), replacing the user id with the tenant id so an
 *    issue is still groupable by blast radius without naming a person.
 * 3. Redacts secret-named keys in the request body (`request.data`).
 * 4. Redacts CAPABILITY tokens out of every URL the event carries (ruling R18)
 *    — the guest table page's `/{slug}/t/{token}` is a bearer credential, and
 *    Sentry attaches the URL to every event without being asked.
 * 5. Masks every gift-card number on a gift-card route, and every PAYMENT card
 *    number (Luhn-valid, network prefix — `../giftCardNumberRedaction`) on
 *    EVERY route, everywhere in the event: body, message, exception values,
 *    breadcrumbs, extra and contexts, spans (SOC 2 R60, PCI-2).
 *
 * Generic over the caller's own event type so it returns EXACTLY what it was
 * given — a `beforeSend` must hand Sentry back its own `ErrorEvent`, and a
 * helper that narrowed the type would be rejected at every call site.
 *
 * Mutates and returns the event, matching Sentry's `beforeSend` contract.
 */
export function scrubSentryEvent<T>(
  event: T | null | undefined,
  env: string | undefined | null,
): T | null {
  if (!event) return null;
  if (isDroppedEnvironment(env)) return null;

  const scrubbable = event as ScrubbableSentryEvent;
  if (scrubbable.user) {
    delete scrubbable.user['email'];
    delete scrubbable.user['username'];
    delete scrubbable.user['ip_address'];
    scrubbable.user['id'] =
      (scrubbable.tags?.['tenantId'] as string | undefined) ?? 'unknown';
  }
  redactEventUrls(scrubbable);
  redactRequestBody(scrubbable);
  redactGiftCardEvent(scrubbable);
  redactPaymentCardEvent(scrubbable);

  return event;
}

/**
 * An event raised while serving a gift-card route (`/gift-card…`) has every
 * 12–19 digit run masked, EVERYWHERE in the event — URL, body, message,
 * exception values, breadcrumbs, spans. A gift card number has no PIN behind
 * it (spec 2026-10-08 §0 D1, §2.2), so the number is the money; listing the
 * fields it might appear in is the list an SDK release adds to silently (see
 * `isUrlShapedKey`'s history), so the whole event is walked instead.
 */
function redactGiftCardEvent(event: ScrubbableSentryEvent): void {
  const transaction = (event as { transaction?: unknown }).transaction;
  if (!isGiftCardPath(event.request?.url) && !isGiftCardPath(transaction)) return;
  redactCardNumbersDeep(event);
}

/**
 * Every payment card number in the event is masked to `[card…1234]`, on EVERY
 * route (SOC 2 R60, `docs/compliance/PCI_DSS_SCOPE.md` PCI-2).
 *
 * Until 2026-11-02 only gift-card routes were masked, and the key filter did
 * not know `pan`, so a 5xx on `/payments/sale` while the POS's native keyed
 * fields sent `{ kind: 'Card', pan }` would have carried the full number to
 * Sentry. The key filter now knows `pan`, but a key list only catches the
 * names someone thought of; a card number in an exception message, a
 * breadcrumb, `extra` or a stringified body has no key at all. So the whole
 * event is walked, as the gift-card rule does — but with the narrower PAYMENT
 * card shape (Luhn-valid, prefix 2–7), so ids and timestamps stay readable.
 */
function redactPaymentCardEvent(event: ScrubbableSentryEvent): void {
  redactPaymentCardNumbersDeep(event);
}

/**
 * Both event hooks a `Sentry.init` needs, from one call.
 *
 * 🔴 WHY THIS EXISTS. `beforeSend` covers ERROR events only. A transaction goes
 * through `beforeSendTransaction`, and a config that wires only the first has a
 * whole second channel reporting raw URLs — with `tracesSampleRate: 0.1` that
 * is roughly one in ten page views, each carrying `request.url`, the Referer
 * header (`httpContextIntegration` writes both on every event) and the span's
 * `url.full`. The capability token was in all three.
 *
 * Returned as one object so a config file makes ONE wiring call and cannot
 * adopt half of it:
 *
 * ```ts
 * Sentry.init({ dsn, environment, ...sentryEventScrubbers(environment) });
 * ```
 *
 * Both hooks run the identical policy — environment drop, PII scrub, URL
 * redaction — because a transaction is as capable of carrying a credential as
 * an error is.
 */
export function sentryEventScrubbers(env: string | undefined | null): {
  beforeSend: <T>(event: T | null | undefined) => T | null;
  beforeSendTransaction: <T>(event: T | null | undefined) => T | null;
} {
  const scrub = <T,>(event: T | null | undefined): T | null =>
    scrubSentryEvent(event, env);
  return { beforeSend: scrub, beforeSendTransaction: scrub };
}

/**
 * Resolves the environment name to report to Sentry from a candidate list,
 * first non-empty wins.
 *
 * Callers pass their runtime's vars in priority order. `VERCEL_ENV` must come
 * before `NODE_ENV`: Vercel sets `NODE_ENV=production` for preview and custom
 * environment builds too, so a `NODE_ENV`-first resolution labels every
 * non-production deployment "production" in the Sentry environment filter.
 */
export function resolveSentryEnvironment(
  ...candidates: (string | undefined | null)[]
): string {
  for (const candidate of candidates) {
    if (candidate) return candidate;
  }
  return 'development';
}
